/**
 * Modal de pedido do garçom — cria ou edita.
 *
 * Criar: canal (mesa/take-away/delivery), mesa quando salão, itens montados
 * pelo cardápio público COM personalização — opcionais (extras com preço) e
 * remoção de ingredientes removíveis, como o cliente faz no cardápio.
 * Editar: pré-carrega itens/notas (personalizações preservadas) e substitui
 * a lista via PATCH /orders/:id — só em pending/confirmed.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ordersApi, publicMenu, tables } from '../lib/api.js'
import type { OrderDetailed, PublicMenuProduct } from '../lib/api.js'
import { config } from '../lib/api/config.js'
import { Button, Input, Modal, Spinner, Textarea, toast } from './ui/index.js'
import { cn } from '../lib/utils.js'
import { useI18n } from '../lib/i18n.js'
import { intlLocaleFor } from '@self-restaurant/shared'

type Channel = 'dine_in' | 'takeaway' | 'delivery'

type DraftExtra = { extra_id: string; quantity: number }

type DraftItem = {
  productId: string
  name: string
  variantId: string | null
  variantLabel: string | null
  basePriceCents: number
  unitPriceCents: number
  quantity: number
  removedIngredientIds: string[]
  removedLabel: string | null
  extras: DraftExtra[]
  extrasLabel: string | null
  /** item já gravado (edição): o PATCH manda o id para manter o preço (#253) */
  orderItemId?: string
  /** observação do item gravado — reenviada para não se perder na edição */
  notes?: string | null
}

function extraUnitPrice(extra: PublicMenuProduct['extras'][number], productPrice: number): number {
  return extra.price_type === 'fixed'
    ? extra.price_value
    : Math.round((productPrice * extra.price_value) / 10000)
}

function cents(cents: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100)
}

export function OrderFormModal({
  open,
  order,
  onClose,
  onSaved,
}: {
  open: boolean
  /** pedido em edição; null = criação */
  order: OrderDetailed | null
  onClose: () => void
  onSaved: () => void
}) {
  const editing = order !== null
  const { t, lang } = useI18n()
  // #419/#413/#428: moeda e país do tenant (público — não dependem do idioma)
  const { data: snapshot } = useQuery({
    queryKey: ['restaurant-country-currency'],
    queryFn: config.countryCurrency,
    staleTime: 10 * 60_000,
  })
  const money = (n: number) =>
    cents(n, snapshot?.currency ?? 'BRL', intlLocaleFor(lang, snapshot?.country) || 'pt')
  const [channel, setChannel] = useState<Channel>('dine_in')
  const [tableId, setTableId] = useState<string>('')
  const [items, setItems] = useState<DraftItem[]>([])
  const [notes, setNotes] = useState('')
  const [reference, setReference] = useState('')
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  /** produto aberto no configurador */
  const [configuring, setConfiguring] = useState<PublicMenuProduct | null>(null)

  useEffect(() => {
    if (!open) return
    setConfiguring(null)
    setSearch('')
    if (order) {
      setChannel(order.channel)
      setTableId(order.table_id ?? '')
      setNotes(order.notes ?? '')
      setReference(order.reference_name ?? '')
      setItems(
        order.items.map((item) => ({
          orderItemId: item.id,
          notes: item.notes,
          productId: item.product_id,
          name: item.product_name,
          variantId: item.variant_id,
          variantLabel: item.variant_label,
          basePriceCents: item.unit_price_cents,
          unitPriceCents: item.unit_price_cents,
          quantity: item.quantity,
          removedIngredientIds: item.removed_ingredient_ids ?? [],
          removedLabel:
            item.removed_ingredients && item.removed_ingredients.length > 0
              ? `${t('admin.orderForm.without')} ${item.removed_ingredients.join(', ')}`
              : null,
          extras: (item.extras ?? []).map((e) => ({
            extra_id: e.extra_id,
            quantity: e.quantity,
          })),
          extrasLabel:
            item.extras && item.extras.length > 0
              ? `+ ${item.extras.map((e) => `${e.quantity}× ${e.name}`).join(', ')}`
              : null,
        })),
      )
    } else {
      setChannel('dine_in')
      setTableId('')
      setItems([])
      setNotes('')
      setReference('')
    }
  }, [open, order])

  const { data: tableList } = useQuery({
    queryKey: ['tables'],
    queryFn: tables.list,
    enabled: open,
  })

  const { data: categories, isLoading: loadingMenu } = useQuery({
    queryKey: ['menu', 'waiter-picker', channel],
    queryFn: () => publicMenu.categories(channel),
    enabled: open,
  })

  const visibleProducts = useMemo(() => {
    const all = (categories ?? []).flatMap((c) => c.products)
    const q = search.trim().toLowerCase()
    return (q ? all.filter((p) => p.name.toLowerCase().includes(q)) : all).slice(0, 30)
  }, [categories, search])

  const total = items.reduce((sum, i) => sum + i.unitPriceCents * i.quantity, 0)
  const customizable = (p: PublicMenuProduct) =>
    p.variants.length > 0 || p.extras.length > 0 || p.ingredients.some((i) => i.is_removable)

  function setQty(idx: number, qty: number) {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((_, i) => i !== idx)
        : prev.map((item, i) => (i === idx ? { ...item, quantity: qty } : item)),
    )
  }

  async function submit() {
    if (items.length === 0) {
      toast.error(t('admin.orderForm.need_item'))
      return
    }
    if (channel === 'dine_in' && !tableId) {
      toast.error(t('admin.orderForm.need_table'))
      return
    }
    if (!reference.trim()) {
      toast.error(t('admin.orderForm.need_reference'))
      return
    }
    setSaving(true)
    const payloadItems = items.map((i) => ({
      product_id: i.productId,
      variant_id: i.variantId,
      quantity: i.quantity,
      removed_ingredient_ids: i.removedIngredientIds,
      extra_ids: i.extras,
    }))
    // edição (#253): linhas já gravadas vão com o id (e a observação) — o
    // servidor mantém o preço/promoção do momento do pedido quando a linha não
    // mudou de produto, variante, extras ou quantidade
    const patchItems = items.map((i, idx) => ({
      ...payloadItems[idx]!,
      ...(i.orderItemId ? { id: i.orderItemId, notes: i.notes ?? null } : {}),
    }))
    try {
      if (editing) {
        await ordersApi.update(order!.id, {
          channel,
          table_id: channel === 'dine_in' ? tableId : null,
          reference_name: reference.trim() || null,
          notes: notes.trim() || null,
          items: patchItems,
        })
        toast.success(t('admin.orderForm.updated'))
      } else {
        await ordersApi.create({
          channel,
          table_id: channel === 'dine_in' ? tableId : null,
          reference_name: reference.trim() || null,
          notes: notes.trim() || null,
          tip_cents: 0,
          items: payloadItems,
        })
        toast.success(t('admin.orderForm.created'))
      }
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.orderForm.save_error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        editing
          ? t('admin.orderForm.edit_title', { id: order!.id.slice(-8).toUpperCase() })
          : t('admin.orderForm.new_title')
      }
      footer={
        <div className="flex gap-3 justify-end items-center w-full">
          <span className="mr-auto text-sm font-bold text-gray-900">{money(total)}</span>
          <Button variant="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button loading={saving} onClick={() => void submit()}>
            {editing ? t('admin.orderForm.save_changes') : t('admin.orderForm.submit')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Canal */}
        <div className="flex gap-2">
          {(
            [
              ['dine_in', t('admin.channel.dine_in')],
              ['takeaway', t('admin.channel.takeaway')],
              ['delivery', t('admin.channel.delivery')],
            ] as Array<[Channel, string]>
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setChannel(key)}
              className={cn(
                'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                channel === key
                  ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {channel === 'dine_in' && (
          <div>
            <label className="text-sm font-medium text-gray-700">
              {t('admin.orderForm.table')}
            </label>
            <select
              value={tableId}
              onChange={(e) => setTableId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">{t('admin.orderForm.select_table')}</option>
              {(tableList ?? []).map((tb) => (
                <option key={tb.id} value={tb.id}>
                  {t('admin.orderForm.table_n', { n: String(tb.number) })}
                  {tb.label ? ` — ${tb.label}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Itens atuais */}
        {items.length > 0 && (
          <ul className="rounded-lg border border-gray-200 divide-y divide-gray-100">
            {items.map((item, idx) => (
              <li key={`${item.productId}-${idx}`} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.name}
                    {item.variantLabel && (
                      <span className="ml-1 text-xs font-semibold text-gray-500">
                        ({item.variantLabel})
                      </span>
                    )}
                  </p>
                  {item.extrasLabel && (
                    <p className="text-xs text-indigo-600 truncate">{item.extrasLabel}</p>
                  )}
                  {item.removedLabel && (
                    <p className="text-xs text-red-600 truncate">{item.removedLabel}</p>
                  )}
                </div>
                <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">
                  {money(item.unitPriceCents * item.quantity)}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setQty(idx, item.quantity - 1)}
                    className="w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    −
                  </button>
                  <span className="w-6 text-center text-sm font-bold">{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQty(idx, item.quantity + 1)}
                    className="w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    +
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Configurador do item (opcionais + remoção) */}
        {configuring && (
          <ItemConfigurator
            product={configuring}
            money={money}
            onAdd={(draft) => {
              setItems((prev) => [...prev, { ...draft }])
              setConfiguring(null)
            }}
            onCancel={() => setConfiguring(null)}
          />
        )}

        {/* Picker de produtos */}
        {!configuring && (
          <div>
            <label className="text-sm font-medium text-gray-700">
              {t('admin.orderForm.add_items')}
            </label>
            {loadingMenu ? (
              <div className="flex justify-center py-4">
                <Spinner className="w-5 h-5 text-indigo-500" />
              </div>
            ) : (
              <>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('admin.orderForm.search_ph')}
                />
                <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {visibleProducts.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() =>
                        customizable(p)
                          ? setConfiguring(p)
                          : setItems((prev) => [
                              ...prev,
                              {
                                productId: p.id,
                                name: p.name,
                                variantId: null,
                                variantLabel: null,
                                basePriceCents: p.price_cents,
                                unitPriceCents: p.price_cents,
                                quantity: 1,
                                removedIngredientIds: [],
                                removedLabel: null,
                                extras: [],
                                extrasLabel: null,
                              },
                            ])
                      }
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-indigo-50 transition-colors"
                    >
                      <span className="text-sm text-gray-900 truncate">
                        {p.name}
                        {customizable(p) && (
                          <span className="ml-2 text-xs text-indigo-500">
                            {t('admin.orderForm.customizable')}
                          </span>
                        )}
                      </span>
                      <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">
                        + {money(p.price_cents)}
                      </span>
                    </button>
                  ))}
                  {visibleProducts.length === 0 && (
                    <p className="px-3 py-4 text-xs text-gray-400 text-center">
                      {t('admin.orderForm.no_products')}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <Input
          label={t('admin.orderForm.reference')}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder={t('admin.orderForm.reference_ph')}
        />
        <Textarea
          label={t('admin.orderForm.notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('admin.orderForm.optional')}
        />
        {editing && <p className="text-xs text-gray-400">{t('admin.orderForm.edit_hint')}</p>}
      </div>
    </Modal>
  )
}

/** Configura um item antes de adicionar: extras com quantidade e remoção
 *  de ingredientes removíveis — o mesmo poder de escolha do cliente. */
function ItemConfigurator({
  product,
  money,
  onAdd,
  onCancel,
}: {
  product: PublicMenuProduct
  /** formatação de preço — moeda do país do tenant (#419/#413) */
  money: (n: number) => string
  onAdd: (draft: DraftItem) => void
  onCancel: () => void
}) {
  const { t } = useI18n()
  const [qty, setQty] = useState(1)
  const [variantId, setVariantId] = useState<string | null>(null)
  const [removed, setRemoved] = useState<string[]>([])
  const [extras, setExtras] = useState<DraftExtra[]>([])

  const removable = product.ingredients.filter((i) => i.is_removable)
  const hasVariants = product.variants.length > 0
  const selectedVariant = product.variants.find((v) => v.id === variantId)

  function toggleRemoved(id: string) {
    setRemoved((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function changeExtraQty(id: string, delta: number) {
    setExtras((prev) => {
      const found = prev.find((e) => e.extra_id === id)
      if (!found) return delta > 0 ? [...prev, { extra_id: id, quantity: 1 }] : prev
      const next = found.quantity + delta
      if (next <= 0) return prev.filter((e) => e.extra_id !== id)
      return prev.map((e) => (e.extra_id === id ? { ...e, quantity: next } : e))
    })
  }

  const extrasCents = extras.reduce((sum, e) => {
    const def = product.extras.find((x) => x.id === e.extra_id)
    return def ? sum + extraUnitPrice(def, product.price_cents) * e.quantity : sum
  }, 0)
  const unit = (selectedVariant ? selectedVariant.price_cents : product.price_cents) + extrasCents

  function add() {
    const removedNames = removed
      .map((id) => product.ingredients.find((i) => i.id === id)?.name)
      .filter((n): n is string => !!n)
    const extrasLabelParts = extras
      .map((e) => {
        const def = product.extras.find((x) => x.id === e.extra_id)
        return def ? `${e.quantity}× ${def.name}` : null
      })
      .filter((n): n is string => !!n)
    onAdd({
      productId: product.id,
      name: product.name,
      variantId,
      variantLabel: selectedVariant?.name ?? null,
      basePriceCents: product.price_cents,
      unitPriceCents: unit,
      quantity: qty,
      removedIngredientIds: removed,
      removedLabel:
        removedNames.length > 0
          ? `${t('admin.orderForm.without')} ${removedNames.join(', ')}`
          : null,
      extras,
      extrasLabel: extrasLabelParts.length > 0 ? `+ ${extrasLabelParts.join(', ')}` : null,
    })
  }

  return (
    <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/40 p-4 space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-gray-900">{product.name}</p>
          {product.description && (
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{product.description}</p>
          )}
        </div>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          ✕
        </button>
      </div>

      {hasVariants && (
        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            {product.variant_group_label ?? t('admin.orderForm.variation')}{' '}
            <span className="text-red-500">*</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {product.variants.map((v) => {
              const active = v.id === variantId
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVariantId(v.id)}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                    active
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {v.name} — {money(v.price_cents)}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {removable.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            {t('admin.orderForm.remove_ingredients')}
          </p>
          <div className="flex flex-wrap gap-2">
            {removable.map((ing) => {
              const off = removed.includes(ing.id)
              return (
                <button
                  key={ing.id}
                  type="button"
                  onClick={() => toggleRemoved(ing.id)}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                    off
                      ? 'border-red-300 bg-red-100 text-red-700 line-through'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {off ? `${t('admin.orderForm.without')} ` : ''}
                  {ing.name}
                </button>
              )
            })}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">{t('admin.orderForm.tap_to_remove')}</p>
        </div>
      )}

      {product.extras.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            {t('admin.orderForm.optionals')}
          </p>
          <div className="space-y-1.5">
            {product.extras.map((extra) => {
              const qtyE = extras.find((e) => e.extra_id === extra.id)?.quantity ?? 0
              const price = extraUnitPrice(extra, product.price_cents)
              return (
                <div
                  key={extra.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 truncate">{extra.name}</p>
                    <p className="text-xs text-gray-500">+ {money(price)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => changeExtraQty(extra.id, -1)}
                      disabled={qtyE === 0}
                      className="w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
                    >
                      −
                    </button>
                    <span className="w-5 text-center text-sm font-bold">{qtyE}</span>
                    <button
                      type="button"
                      onClick={() => changeExtraQty(extra.id, 1)}
                      className="w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                      +
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{t('admin.orderForm.qty')}</span>
          <button
            type="button"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            −
          </button>
          <span className="w-6 text-center text-sm font-bold">{qty}</span>
          <button
            type="button"
            onClick={() => setQty((q) => q + 1)}
            className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            +
          </button>
        </div>
        <Button onClick={add} disabled={hasVariants && !selectedVariant}>
          {hasVariants && !selectedVariant
            ? t('admin.orderForm.choose_variation')
            : t('admin.orderForm.add_price', { price: money(unit * qty) })}
        </Button>
      </div>
    </div>
  )
}
